
import _ from 'lodash'
import { defineComponent, PropType, ref, computed, watch, onMounted } from 'vue'
import { UUID } from '@Protium/vue-ozone-utils/types/utils'
import { format } from '@/utils/template'

import ListHeader from '@/components/ListHeader.vue'
type TenantType = 'tenant'

import { LocalStorage } from 'quasar'
import { CBOOL_FALSE, CBOOL_TRUE, DEFAULT_TAB_OPTIONS, ItemViewMode, ORDER_STAGE_OPTIONS, USER_SETTINGS_CNAMES } from '@/constants'
import { useStore } from 'vuex'
import { Financial } from '@/models/interfaces/company-details'

export enum OrderStage {
  New = 'new',
  Picked = 'picked',
  Fulfilled = 'fulfilled',
  Shipped = 'Shipped',
  Returned = 'returned'
}

export interface AppGlobalSettings {
  sourcing: {
    deliverableAlertThreshold: number
  },
  orders: {
    printPickingLabel: boolean,
    printPickingLabelAtStage: OrderStage,
    printShipmentLabelAtStage: OrderStage,
    printPackingSlipAtStage: OrderStage
  },
  products: {
    metaFields: Array<{
      key: string,
      type: string
    }>
  }
}

export const addressModel = {
  id: '',
  name: '',
  subtitle: '',
  address: {
    street: '',
    streetNumber: '',
    addition: '',
    city: '',
    zip: '',
    country: '',
    country_iso: '',
    region: '',
    department: ''
  },
  contact: {
    email: '',
    phone: ''
  },
  registration: {
    registrar: 'kvk',
    label: 'KVK',
    id: '0'
  },
  tax: {
    registrar: 'belastingdienst',
    label: '',
    id: '0'
  }
}

export const applicationSettingsModel:AppGlobalSettings = {
  sourcing: {
    deliverableAlertThreshold: 10
  },
  orders: {
    printPickingLabel: false,
    printPickingLabelAtStage: OrderStage.Picked,
    printShipmentLabelAtStage: OrderStage.Fulfilled,
    printPackingSlipAtStage: OrderStage.Fulfilled
  },
  products: {
    metaFields: []
  }
}

export interface Tenant {
  sys?: {
    id: UUID,
    type: TenantType
  },
  organisation?: string,
  id: string,
  version: string,
  config_version: string,
  settings?: {
    private_services?: Array<string>,
    financial?: Record<Financial, any>,
    application?: typeof applicationSettingsModel,
    company?: typeof addressModel
  }
}

export const DEFAULT_TENANT_SETTINGS = {
  application: applicationSettingsModel,
  company: addressModel
}

export default defineComponent({
  name: 'TenantConfigEditor',
  components: { ListHeader },
  emits: ['update:modelValue','edit', 'save'],
  props: {
    modelValue: {
      type: Object as PropType<Tenant>,
      required: true
    },
    editing: {
      type: Boolean
    },
    model: {
      type: Object,
      default: () => null
    },
    request: {
      type: Object,
      default: () => null
    },
    readonly: {
      type: Boolean
    }
  },
  setup (props, { emit }) {
    const $store = useStore()
    const localEditing = ref(false)
    const localSaving = ref(false)
    // console.log({...props.modelValue})
    const data = ref(_.defaultsDeep({...(props.modelValue || {})}, { settings: DEFAULT_TENANT_SETTINGS }))

    // fetch user settings from cookie...
    const storedSettings = $store.getters['User/Settings']

    const userSettings = ref({
      defaultTab: DEFAULT_TAB_OPTIONS.find(option => option.value === storedSettings.defaultTab),
      hideResolved: storedSettings.hideResolved,
      newFirst: storedSettings.newFirst,
      orderViewMode: storedSettings.orderViewMode,
      orderAutoAdvance: storedSettings.orderAutoAdvance,
      showNewAsPickable: storedSettings.showNewAsPickable,
      showNewAsPackable: storedSettings.showNewAsPackable
    })

    watch(() => props.modelValue, value => {
      data.value = _.defaultsDeep({...(value || {})}, { settings: DEFAULT_TENANT_SETTINGS })
      if (!data.value.settings) data.value.settings = DEFAULT_TENANT_SETTINGS
    })

    onMounted(() => {
      if (!data.value.settings) data.value.settings = DEFAULT_TENANT_SETTINGS
      emit('update:modelValue', data.value)
    })

    async function save () {
      if (!props.request) throw Error('No request handler prop was passed to TenantConfigEditor')
      const saved = await props.request.patch('/service/ctrl/tenant', undefined, data.value)
      // console.log('tenant saved', saved)

      localEditing.value = false
      emit('update:modelValue', data.value)
    }

    return {
      format,
      data,
      // edit: e => emit('edit', e),
      userSettings,
      storedSettings,
      localEditing,
      localSaving,
      save,
      edit: e => localEditing.value = e,
      gridStyle: computed(() => `grid-template-columns: ${localEditing.value ? '50% [col-start] 50%;' : '120px [col-start] 235px;'}`),
      DEFAULT_TAB_OPTIONS,
      ORDER_STAGE_OPTIONS,
      ORDER_VIEWMODE_OPTIONS: Object.values(ItemViewMode).filter(f => typeof f === 'string').map(k => ({label: k, value: ItemViewMode[k]})),
      setSettingCookie: (key, value) => {
        LocalStorage.set(USER_SETTINGS_CNAMES[key], typeof value === 'boolean' ? (value ? CBOOL_TRUE : CBOOL_FALSE) : typeof value === 'object' && value.value ? value.value : value)
        void $store.dispatch('User/UpdateSetting', {key, value: typeof value === 'object' && value.value ? value.value : value})
      }
    }
  }
})
